import { forwardRef, useEffect, useState } from 'react';
import { toAgorot, fromAgorot, formatILS } from '@shared/money.ts';
import { cn } from '@/lib/utils';

/**
 * Amount input. The value is INTEGER AGOROT (or null when empty); conversion happens only here,
 * at the edge (CLAUDE.md invariant 1). Accepts "180", "1,234.50", "12,5".
 */
export const MoneyInput = forwardRef<HTMLInputElement, {
  value: number | null;
  onChange: (agorot: number | null) => void;
  id?: string; disabled?: boolean; placeholder?: string; className?: string; allowNegative?: boolean;
  'aria-invalid'?: boolean;
}>(({ value, onChange, allowNegative, className, ...props }, ref) => {
  const [text, setText] = useState(value == null ? '' : String(fromAgorot(value)));
  const [bad, setBad] = useState(false);

  useEffect(() => {
    // Sync when the value changes from outside (reset, load).
    let current: number | null = null;
    try { current = text.trim() ? toAgorot(text) : null; } catch { current = NaN; }
    if (current !== value) setText(value == null ? '' : String(fromAgorot(value)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const commit = (s: string) => {
    setText(s);
    if (!s.trim()) { setBad(false); onChange(null); return; }
    try {
      const a = toAgorot(s);
      if (!allowNegative && a < 0) throw new Error('negative');
      setBad(false);
      onChange(a);
    } catch {
      setBad(true);
    }
  };

  return (
    <div className={cn('relative', className)}>
      <input
        ref={ref}
        inputMode="decimal"
        dir="ltr"
        autoComplete="off"
        value={text}
        onChange={(e) => commit(e.target.value)}
        onBlur={() => { if (!bad && value != null) setText(String(fromAgorot(value))); }}
        aria-invalid={bad || props['aria-invalid']}
        className="h-11 w-full rounded-xl border border-border bg-surface ps-8 pe-3 text-end text-[15px] tabular-nums focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15 aria-[invalid=true]:border-danger"
        {...props}
      />
      {/* dir=ltr input: the shekel sign sits on its left edge */}
      <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-muted">₪</span>
      {bad ? <p className="mt-1 text-sm text-danger">סכום לא תקין</p> : null}
    </div>
  );
});
MoneyInput.displayName = 'MoneyInput';

/** Display an agorot amount ("‏1,234.50 ₪"); negatives in red. */
export function Money({ agorot, className }: { agorot: number; className?: string }) {
  return <bdi className={cn('tabular-nums whitespace-nowrap', agorot < 0 && 'text-danger', className)}>{formatILS(agorot)}</bdi>;
}
